import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pin, X } from 'lucide-react';
import api from '../utils/api';

// Status pill colors mirror the rest of the app's status palette.
const STATUS_COLORS = {
  new: '#3b82f6',
  open: '#3b82f6',
  pending: '#f59e0b',
  resolved: '#22c55e',
  closed: '#6b7280',
  validation: '#a855f7',
};

// Compact list of pinned tickets shown in the header. Click a chip to navigate;
// a small dot signals that the ticket has activity newer than the user's last view
// (green = new activity, gray = up to date). Unpin via the X button on hover.
export default function PinnedTicketsBar({ apiBase = '/ticket-pins', detailPathPrefix = '/tickets' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [pins, setPins] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await api.get(apiBase);
      setPins(res.data?.pins || []);
    } catch {
      setPins([]);
    }
  }, [apiBase]);

  useEffect(() => {
    load();
    const onChanged = () => load();
    window.addEventListener('ticket-pins:changed', onChanged);
    // Light polling so other tabs / inbound replies refresh the new-activity dot.
    const iv = setInterval(load, 60_000);
    return () => {
      window.removeEventListener('ticket-pins:changed', onChanged);
      clearInterval(iv);
    };
  }, [load]);

  // Refresh when the route changes — e.g. after viewing a pinned ticket the
  // server marked it seen, so the dot should clear when the user comes back.
  useEffect(() => { load(); }, [location.pathname, load]);

  const unpin = useCallback(async (e, ticketId) => {
    e.stopPropagation();
    try {
      await api.delete(`${apiBase}/${ticketId}`);
      setPins(prev => prev.filter(p => p.ticket_id !== ticketId));
      window.dispatchEvent(new CustomEvent('ticket-pins:changed'));
    } catch {}
  }, [apiBase]);

  if (!pins.length) return null;

  return (
    <div
      className="pinned-tickets-bar"
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        flex: '0 1 auto', minWidth: 0, overflowX: 'auto',
        scrollbarWidth: 'none',
      }}
    >
      <Pin size={12} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />
      {pins.map(p => {
        const hasNew = (p.unseen_count || 0) > 0;
        const dotColor = hasNew ? '#22c55e' : '#9ca3af';
        const statusColor = STATUS_COLORS[p.status] || '#6b7280';
        return (
          <div
            key={p.ticket_id}
            onClick={() => navigate(`${detailPathPrefix}/${p.ticket_id}`)}
            title={`#${p.ticket_number} — ${p.subject || ''}${p.company_name ? ` · ${p.company_name}` : ''}${hasNew ? ` · ${t('tickets.newActivity')}` : ''}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 8px 4px 8px', borderRadius: 999,
              background: 'var(--c-surface-2)',
              border: `1px solid ${hasNew ? '#22c55e' : 'var(--c-border)'}`,
              cursor: 'pointer', flexShrink: 0,
              fontSize: 12, color: 'var(--c-text)',
              maxWidth: 220,
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: dotColor, flexShrink: 0,
              boxShadow: hasNew ? '0 0 0 3px rgba(34,197,94,0.18)' : undefined,
            }} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--c-text-muted)', flexShrink: 0 }}>
              #{p.ticket_number}
            </span>
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontWeight: hasNew ? 600 : 500,
            }}>
              {p.subject || ''}
            </span>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: statusColor, flexShrink: 0,
            }} title={p.status} />
            <button
              onClick={(e) => unpin(e, p.ticket_id)}
              className="btn btn-ghost btn-icon"
              style={{ padding: 2, height: 16, width: 16, marginLeft: 2, flexShrink: 0 }}
              title={t('tickets.unpin')}
              aria-label={t('tickets.unpin')}
            >
              <X size={11} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
